import _ from 'lodash-es';
import { assign as _assign, get as _get, forOwn as _forOwn } from 'lodash-es';
import CompatibleWithHelper from '../components/utils/CompatibleWithHelper';
/**
 * This object encapsulates the interaction with the Compatio API
 */
const DCABSdataApi = {
  /**
   * An object that stores a cache of all requests to retrieve data from the Compatio API.
   * The reason we're doing this is because a user could trigger an API call multiple times before the
   * initial request is returned from the server, so by caching the initial request for the product data,
   * we can avoid additional requests
   */
  categoryRetrievalPromiseCache: {},

  targetGraphApiEndpoint: [
    {
      Region: 'Live',
      label: 'prod-live',
      Url: 'https://spx.compat.io/v0/searchResultsLambdaMAP',
    },
    {
      Region: 'LiveV2',
      label: 'prod-live',
      Url: 'https://spx-v2.compat.io/v0/searchResultsLambdaMAP',
    },
    {
      Region: 'LiveV2-api',
      label: 'prod-live',
      Url: 'https://smartbuilder.compat.io/',
    },
    {
      Region: 'Master-9',
      label: 'prod-master',
      Url: 'https://spx-qa.compat.io/v0/searchResultsLambdaMAP',
    },
    {
      Region: 'Stage',
      label: 'stage',
      Url: 'https://spx-qa.compat.io/v0/searchResultsLambdaMAP',
    },
    {
      Region: 'MasterV2',
      label: 'prod-master',
      Url: 'https://smartbuilder.compat.io/',
    },
    {
      Region: 'Master',
      label: 'prod-master',
      Url: 'https://smartbuilder.compat.io/',
    },
    {
      Region: 'MasterV2Dev',
      label: 'prod-master',
      Url: 'https://smartbuilder.compat.io/',
    },
    {
      Region: 'Dev',
      label: 'prod-master',
      Url: 'https://8xj2musshl.execute-api.us-east-2.amazonaws.com/compatioxrs',
    },
    {
      Region: 'QA',
      label: 'prod-master',
      Url: 'https://m6a9hz8t5d.execute-api.us-east-2.amazonaws.com/qa',
    },
    { Region: 'Preview', label: 'prod-master', Url: '' },
  ],
  // TODO: Update this to the final API url (some subdomain of compat.io)
  apiEndpoint:
    'https://1ciuemjzva.execute-api.us-east-2.amazonaws.com/compatio_xrs',

  shopifyApiEndpointProd:
    'https://9h6rg5qonl.execute-api.us-east-2.amazonaws.com/prod-live',

  shopifyApiEndpointQA:
    'https://m6a9hz8t5d.execute-api.us-east-2.amazonaws.com/qa',

  /**
   * Reset object to its initial state. Useful for automated testing.
   */
  reset: function() {
    this.categoryRetrievalPromiseCache = {};
  },

  /**
   * Retrieve Compatio compatibility data for a specific merchantProductSku
   *
   * @param merchantProductSku
   * @returns {Promise<any>}
   */
  getCategoryDataByProductSku: async function(
    merchantProductSku,
    categoryLookingAt,
    filterOptions = {},
    defaultModel
  ) {
    const retrieveCategoryDataPromise = await this._getCategoryData(
      merchantProductSku,
      categoryLookingAt,
      filterOptions,
      defaultModel
    );
    return retrieveCategoryDataPromise;
  },

  getCategoryDataByCategory: async function(
    merchantProductSku,
    categoryLookingAt,
    filterOptions = {},
    defaultModel
  ) {
    // alert(categoryLookingAt)
    const retrieveCategoryDataPromise = await this._getCategoryDataByCat(
      merchantProductSku,
      categoryLookingAt,
      filterOptions,
      defaultModel
    );
    return retrieveCategoryDataPromise;
  },

  /**
   * Return a Promise that will return the category data
   * @param merchantProductSku
   * @returns {Promise<any>}
   * @private
   */
  async _getCategoryData(
    merchantProductSku,
    categoryLookingAt,
    filterOptions,
    defaultModel
  ) {
    const locale = window.compatioConfig.locale;
    const IsPlatformShopify =
      typeof _get(window, 'compatioConfig.shopify') === 'object'
        ? 'shopify'
        : 'magento';
    let merchantKey = '';
    let fullApiEndpoint = '';
    let targetGraph = '';
    let modelId = '';
    let v2Key = '';
    if (IsPlatformShopify == 'shopify') {
      merchantKey = _get(window, 'compatioConfig.shopify.merchantKey');
      targetGraph = _get(window, 'compatioConfig.shopify.targetGraph');
      v2Key = _get(window, 'compatioConfig.shopify.v2Key');
      let model = CompatibleWithHelper.getByKey(
        CompatibleWithHelper.keyMap.modelId
      );
      modelId = model === undefined ? defaultModel : model;

      fullApiEndpoint = `${this.targetGraphApiEndpoint.find(
        o => o.Region === targetGraph
      ).Url +
        `v2?builder_model=${modelId}&merchant_key=${merchantKey}&variant_list=${JSON.stringify(
          merchantProductSku
        )}`}`;
      // console.log(JSON.stringify(merchantProductSku), 'fullApiEndpoint');
      if (categoryLookingAt !== '') {
        fullApiEndpoint = `${fullApiEndpoint}`;
      }
      let filterString = '&filter={';
      // if (filterOptions.length >= 1) {
      // console.log(filterOptions,'filterOptions')
      if (filterOptions && filterOptions.length !== 0) {
        let SelectedFilters = [];
        let group = _.groupBy(filterOptions, 'attribute');

        _forOwn(group, (filterType, key) => {
          let filterValues = [];
          filterType.forEach(each => {
            if (key === each.attribute) filterValues.push(each.metaValue);
          });

          SelectedFilters.push({ filtertype: key, values: filterValues });
        });
        SelectedFilters.forEach(element => {
          // console.log(element, 'element');
          filterString += `"${element.filtertype}": ${JSON.stringify(
            element.values
          )},`;
        });
        filterString = `${filterString.slice(0, -1)}}`;
        if (SelectedFilters.length !== 0) {
          fullApiEndpoint = `${fullApiEndpoint}${filterString}`;
          // console.log(SelectedFilters, 'fullApiEndpoint');
        }
      }

      let data = {
        graph_label: this.targetGraphApiEndpoint.find(
          o => o.Region === targetGraph
        ).label,
        merchant_key: merchantKey,
        model_id: modelId,
        variant_id:
          merchantProductSku.length > 0
            ? merchantProductSku.filter(each => each !== undefined)
            : [],
      };
      let urlcheck = `${
        this.targetGraphApiEndpoint.find(o => o.Region === targetGraph).Url
      }`;
      // console.log(urlcheck.split('.')[0].split('//')[1]);
      // alert(v2Key);
      let response = await fetch(fullApiEndpoint, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          // auth_token: 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
          // auth_token: 'mujamgpYmdeza4HtegLTHQWvmZeaf6',
          auth_token: v2Key ? v2Key : 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
        },
        //body: JSON.stringify(data),
      }).catch(error => {
        return new Error(error);
      });
      if (response && !response.ok) {
        return new Error('HTTP error, status = ' + response.status);
      }

      response = await response.json();
      if (response !== undefined) {
        // console.log(response,'response')
        return response;
      } else {
        return [];
      }
    } else if (IsPlatformShopify == 'magento') {
      // merchantKey = _get(window, 'compatioConfig.shopify.merchantKey');
      // targetGraph = _get(window, 'compatioConfig.shopify.targetGraph');

      merchantKey = _get(window, 'compatioConfig.shopify.merchantKey')
        ? _get(window, 'compatioConfig.shopify.merchantKey')
        : _get(window, 'compatioConfig.magento.merchantKey');
      targetGraph = _get(window, 'compatioConfig.shopify.targetGraph')
        ? _get(window, 'compatioConfig.shopify.targetGraph')
        : _get(window, 'compatioConfig.magento.targetGraph');
      v2Key = _get(window, 'compatioConfig.shopify.v2Key')
        ? _get(window, 'compatioConfig.shopify.v2Key')
        : _get(window, 'compatioConfig.magento.v2Key');
      let model = CompatibleWithHelper.getByKey(
        CompatibleWithHelper.keyMap.modelId
      );
      // alert(model+'/'+defaultModel)
      modelId = model === undefined ? defaultModel : model;

      fullApiEndpoint = `${this.targetGraphApiEndpoint.find(
        o => o.Region === targetGraph
      ).Url +
        `v2?builder_model=${modelId}&merchant_key=${merchantKey}&variant_list=${JSON.stringify(
          merchantProductSku
        )}`}`;
      // console.log(JSON.stringify(merchantProductSku), 'fullApiEndpoint');
      if (categoryLookingAt !== '') {
        fullApiEndpoint = `${fullApiEndpoint}`;
      }
      let filterString = '&filter={';
      // if (filterOptions.length >= 1) {
      if (filterOptions && filterOptions.length !== 0) {
        let SelectedFilters = [];
        let group = _.groupBy(filterOptions, 'attribute');

        _forOwn(group, (filterType, key) => {
          let filterValues = [];
          filterType.forEach(each => {
            if (key === each.attribute) filterValues.push(each.metaValue);
          });

          SelectedFilters.push({ filtertype: key, values: filterValues });
        });
        SelectedFilters.forEach(element => {
          // console.log(element, 'element');
          filterString += `"${element.filtertype}": ${JSON.stringify(
            element.values
          )},`;
        });
        filterString = `${filterString.slice(0, -1)}}`;
        if (SelectedFilters.length !== 0) {
          fullApiEndpoint = `${fullApiEndpoint}${filterString}`;
          // console.log(SelectedFilters, 'fullApiEndpoint');
        }
      }

      let data = {
        graph_label: this.targetGraphApiEndpoint.find(
          o => o.Region === targetGraph
        ).label,
        merchant_key: merchantKey,
        model_id: modelId,
        variant_id:
          merchantProductSku.length > 0
            ? merchantProductSku.filter(each => each !== undefined)
            : [],
      };
      let urlcheck = `${
        this.targetGraphApiEndpoint.find(o => o.Region === targetGraph).Url
      }`;
      // console.log(urlcheck.split('.')[0].split('//')[1]);
      // alert(v2Key);
      let response = await fetch(fullApiEndpoint, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          // auth_token: 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
          // auth_token: 'mujamgpYmdeza4HtegLTHQWvmZeaf6',
          auth_token: v2Key ? v2Key : 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
        },
        //body: JSON.stringify(data),
      }).catch(error => {
        return new Error(error);
      });
      if (response && !response.ok) {
        return new Error('HTTP error, status = ' + response.status);
      }

      response = await response.json();
      if (response !== undefined) {
        // console.log(response,'response')
        return response;
      } else {
        return [];
      }
    } else {
      fullApiEndpoint = `${this.apiEndpoint}?gmsProductID=${merchantProductSku}&locale=${locale}`;
      return [];
    }
    // let data = {
    //   graph_label: this.targetGraphApiEndpoint.find(
    //     o => o.Region === targetGraph
    //   ).label,
    //   merchant_key: merchantKey,
    //   model_id: modelId,
    //   variant_id:
    //     merchantProductSku.length > 0
    //       ? merchantProductSku.filter(each => each !== undefined)
    //       : [],
    // };
    // let response = await fetch(fullApiEndpoint, {
    //   method: 'POST',
    //   headers: {
    //     'Content-Type': 'application/json',
    //   },
    //   body: JSON.stringify(data),
    // }).catch(error => {
    //   return new Error(error);
    // });
    // if (response && !response.ok) {
    //   return new Error('HTTP error, status = ' + response.status);
    // }
    // response = await response.json();
    // if (response !== undefined) return response;
    // return [];
  },

  async _getCategoryDataByCat(
    merchantProductSku,
    categoryLookingAt,
    filterOptions,
    defaultModel
  ) {
    const locale = window.compatioConfig.locale;
    const IsPlatformShopify =
      typeof _get(window, 'compatioConfig.shopify') === 'object'
        ? 'shopify'
        : 'magento';
    let merchantKey = '';
    let fullApiEndpoint = '';
    let targetGraph = '';
    let modelId = '';
    let v2Key = '';
    // alert(IsPlatformShopify)
    if (IsPlatformShopify == 'shopify') {
      v2Key = _get(window, 'compatioConfig.shopify.v2Key');
      merchantKey = _get(window, 'compatioConfig.shopify.merchantKey');
      targetGraph = _get(window, 'compatioConfig.shopify.targetGraph');
      let model = CompatibleWithHelper.getByKey(
        CompatibleWithHelper.keyMap.modelId
      );
      modelId = model === undefined ? defaultModel : model;

      fullApiEndpoint = `${this.targetGraphApiEndpoint.find(
        o => o.Region === targetGraph
      ).Url +
        `v2?builder_model=${modelId}&merchant_key=${merchantKey}&starting_component=${categoryLookingAt}`}`;
      // console.log(JSON.stringify(merchantProductSku), 'fullApiEndpoint');
      if (categoryLookingAt !== '') {
        fullApiEndpoint = `${fullApiEndpoint}`;
      }

      /*
      let filterString = '&filter={';
      // if (filterOptions.length >= 1) {
      if (filterOptions && filterOptions.length !== 0) {
        let SelectedFilters = [];
        let group = _.groupBy(filterOptions, 'attribute');

        _forOwn(group, (filterType, key) => {
          let filterValues = [];
          filterType.forEach(each => {
            if (key === each.attribute) filterValues.push(each.metaValue);
          });

          SelectedFilters.push({ filtertype: key, values: filterValues });
        });
        SelectedFilters.forEach(element => {
          // console.log(element, 'element');
          filterString += `"${element.filtertype}": ${JSON.stringify(
            element.values
          )},`;
        });
        filterString = `${filterString.slice(0, -1)}}`;
        if (SelectedFilters.length !== 0) {
          fullApiEndpoint = `${fullApiEndpoint}${filterString}`;
          // console.log(SelectedFilters, 'fullApiEndpoint');
        }
      } 

      let data = {
        graph_label: this.targetGraphApiEndpoint.find(
          o => o.Region === targetGraph
        ).label,
        merchant_key: merchantKey,
        model_id: modelId,
        variant_id:
          merchantProductSku.length > 0
            ? merchantProductSku.filter(each => each !== undefined)
            : [],
      };*/

      // alert(fullApiEndpoint);
      let response = await fetch(fullApiEndpoint, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          // auth_token: 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
          // auth_token: 'mujamgpYmdeza4HtegLTHQWvmZeaf6',
          auth_token: v2Key ? v2Key : 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
        },
        //body: JSON.stringify(data),
      }).catch(error => {
        return new Error(error);
      });
      if (response && !response.ok) {
        return new Error('HTTP error, status = ' + response.status);
      }

      response = await response.json();
      if (response !== undefined) {
        // console.log(response,'response')
        return response;
      } else {
        return [];
      }
    } else if (IsPlatformShopify == 'magento') {
      v2Key = _get(window, 'compatioConfig.shopify.v2Key');
      // merchantKey = _get(window, 'compatioConfig.shopify.merchantKey');
      // targetGraph = _get(window, 'compatioConfig.shopify.targetGraph');

      merchantKey = _get(window, 'compatioConfig.shopify.merchantKey')
        ? _get(window, 'compatioConfig.shopify.merchantKey')
        : _get(window, 'compatioConfig.magento.merchantKey');
      targetGraph = _get(window, 'compatioConfig.shopify.targetGraph')
        ? _get(window, 'compatioConfig.shopify.targetGraph')
        : _get(window, 'compatioConfig.magento.targetGraph');
      let model = CompatibleWithHelper.getByKey(
        CompatibleWithHelper.keyMap.modelId
      );
      modelId = model === undefined ? defaultModel : model;

      fullApiEndpoint = `${this.targetGraphApiEndpoint.find(
        o => o.Region === targetGraph
      ).Url +
        `v2?builder_model=${modelId}&merchant_key=${merchantKey}&starting_component=${categoryLookingAt}`}`;
      // console.log(JSON.stringify(merchantProductSku), 'fullApiEndpoint');
      if (categoryLookingAt !== '') {
        fullApiEndpoint = `${fullApiEndpoint}`;
      }

      let filterString = '&filter={';
      // if (filterOptions.length >= 1) {
      if (filterOptions && filterOptions.length !== 0) {
        let SelectedFilters = [];
        let group = _.groupBy(filterOptions, 'attribute');

        _forOwn(group, (filterType, key) => {
          let filterValues = [];
          filterType.forEach(each => {
            if (key === each.attribute) filterValues.push(each.metaValue);
          });

          SelectedFilters.push({ filtertype: key, values: filterValues });
        });
        SelectedFilters.forEach(element => {
          // console.log(element, 'element');
          filterString += `"${element.filtertype}": ${JSON.stringify(
            element.values
          )},`;
        });
        filterString = `${filterString.slice(0, -1)}}`;
        if (SelectedFilters.length !== 0) {
          fullApiEndpoint = `${fullApiEndpoint}${filterString}`;
          // console.log(SelectedFilters, 'fullApiEndpoint');
        }
      }

      let data = {
        graph_label: this.targetGraphApiEndpoint.find(
          o => o.Region === targetGraph
        ).label,
        merchant_key: merchantKey,
        model_id: modelId,
        variant_id:
          merchantProductSku.length > 0
            ? merchantProductSku.filter(each => each !== undefined)
            : [],
      };
      // alert(fullApiEndpoint);
      let response = await fetch(fullApiEndpoint, {
        method: 'GET',
        headers: {
          Accept: '*/*',
          // auth_token: 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
          // auth_token: 'mujamgpYmdeza4HtegLTHQWvmZeaf6',
          auth_token: v2Key ? v2Key : 'cTi9gLaSomyYvXhuMHQx8ilb3EhjTzx',
        },
        //body: JSON.stringify(data),
      }).catch(error => {
        return new Error(error);
      });
      if (response && !response.ok) {
        return new Error('HTTP error, status = ' + response.status);
      }

      response = await response.json();
      if (response !== undefined) {
        // console.log(response,'response')
        return response;
      } else {
        return [];
      }
    } else {
      fullApiEndpoint = `${this.apiEndpoint}?gmsProductID=${merchantProductSku}&locale=${locale}`;
      return [];
    }
  },

  /**
   * Extract unique product IDs from all categories from Compatio category data
   *
   * @param compatioCategoryData
   * @returns {any[]}
   */
  extractAllProductSkus: function(compatioCategoryData) {
    const productSkus = compatioCategoryData.flatMap(category => {
      return category.products.map(product => {
        return product.merchantProductId;
      });
    });
    // Return unique product IDs
    return [...new Set(productSkus)];
  },

  /**
   * Extract unique product IDs from a specific Compatio category
   *
   * @param compatioCategoryData
   * @param categoryId
   * @returns {any[]}
   */
  /*
  extractProductSkusByCategoryId: function(compatioCategoryData, categoryId) {
    let selectedCategory;
    // alert(categoryId)
    console.log(categoryId, 'categoryId');
    if (categoryId == '[object Object]') {
      selectedCategory = compatioCategoryData.map(categoryObject => {
        if (
          categoryObject.groupset.toLowerCase() ==
          categoryId.unique_name.toLowerCase()
        ) {
          return categoryObject.variant_id;
        }
      });
    } else {
      selectedCategory = compatioCategoryData.map(categoryObject => {
        if (categoryObject.groupset.split('__')[2] == categoryId) {
          console.log(categoryObject,'categoryObject')
          return categoryObject.variant_id;
        }
      });
    }

    // console.log(
    //   compatioCategoryData,
    //   'compatioCategoryData',
    //   categoryId,
    //   selectedCategory
    // );

    let productSkus = [];

    //Abhishek- commenting for the new API changes
    // if (selectedCategory && selectedCategory.products.length) {
    //   productSkus = selectedCategory.products.map(product => {
    //     return product.variant_id;
    //   });
    // }

    // Return unique product IDs
    return [...new Set(selectedCategory)];
  },
*/
  extractProductSkusByCategoryId: function(compatioCategoryData, categoryId) {
    let selectedCategory;
    // alert(categoryId)
    // console.log(categoryId, 'categoryId');
    selectedCategory = compatioCategoryData.map(categoryObject => {
      return categoryObject.variant_id;
    });

    // console.log(
    //   compatioCategoryData,
    //   'compatioCategoryData',
    //   categoryId,
    //   selectedCategory
    // );

    let productSkus = [];

    //Abhishek- commenting for the new API changes
    // if (selectedCategory && selectedCategory.products.length) {
    //   productSkus = selectedCategory.products.map(product => {
    //     return product.variant_id;
    //   });
    // }

    // Return unique product IDs
    return [...new Set(selectedCategory)];
  },
  //extractProductSkusByCategoryIdNotSelected

  /**
   * Merge Compatio product data with Ecommerce product data
   *
   * This allows Compatio to pass back:
   * 1. Sorting data
   * 2. "extraData" that will be displayed to the user
   *
   * @param selectedProductData
   * @param compatioCategoryData
   * @param categoryId
   * @returns {*}
   */
  mergeCompatioDataWithProductData: function(
    selectedProductData,
    compatioCategoryData
  ) {
    // console.log(
    //   'mergeCompatioDataWithProductData',
    //   selectedProductData,
    //   compatioCategoryData
    // );
    const mergedProductData = selectedProductData.map(product => {
      // console.log(product, 'product',selectedProductData,compatioCategoryData);
      const productDataResultApi = [];
      for (const [key, value] of Object.entries(compatioCategoryData.results)) {
        // console.log(key,value);
        value.forEach(element => {
          element = {
            abs_string: element.abs_string,
            astring: element.abs_string,
            image: element.image,
            name: element.name,
            parent_product_id: element.parent_product_id,
            product: element.product,
            variant_id: element.variant_id,
            groupset: key,
            categoryName: key,
            compatioScore: element.score,
          };
          productDataResultApi.push(element);
        });
      }
      const matchingCompatioProduct = productDataResultApi.find(
        compatioProduct => {
          return compatioProduct.variant_id === product.merchantProductSku;
        }
      );

      if (matchingCompatioProduct) {
        // Merge Ecommerce + Compatio data
        _assign(product, matchingCompatioProduct);
      }

      return product;
    });
    return mergedProductData;
  },
};

// export to the global namespace to allow methods to be overridden from code that doesn't import this class directly
window.DCABSdataApi = DCABSdataApi;

export default DCABSdataApi;
